import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiTrendingUp,
  FiClock,
  FiPackage,
  FiFolder,
  FiZap,
  FiArrowUp,
  FiRefreshCw,
  FiCalendar,
  FiActivity,
  FiCheckCircle,
  FiUsers,
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import {
  useUsageDashboard,
  getQuotaPercentage,
  getQuotaColor,
  formatTrialDaysRemaining
} from '../../hooks/useUsageDashboard';
import { getPlanName } from '../../hooks/useSubscription';
import { getUsageHistory, type UsageHistoryEntry } from '../../api/subscriptions';

// Types
interface QuotaInfo {
  limit: number;
  used: number;
  remaining: number;
  unlimited: boolean;
  resets_at?: string;
}

// Composant Progress Circle animé
const CircularProgress: React.FC<{
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color: string;
  label: string;
  value: string;
  sublabel?: string;
}> = ({ percentage, size = 140, strokeWidth = 10, color, label, value, sublabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const colorMap: Record<string, string> = {
    green: '#22c55e',
    yellow: '#eab308',
    orange: '#f97316',
    red: '#ef4444',
    blue: '#3b82f6',
    purple: '#a855f7'
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress circle with animation */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colorMap[color] || colorMap.blue}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {sublabel && <span className="text-xs text-gray-500">{sublabel}</span>}
        </div>
      </div>
      <span className="mt-2 text-sm font-medium text-gray-700">{label}</span>
    </div>
  );
};

// Composant barre de progression amélioré
const EnhancedProgressBar: React.FC<{
  percentage: number;
  color: string;
  showPulse?: boolean;
  height?: number;
}> = ({ percentage, color, showPulse = false, height = 8 }) => {
  const colorMap: Record<string, { bg: string; bar: string }> = {
    green: { bg: 'bg-green-100', bar: 'bg-green-500' },
    yellow: { bg: 'bg-yellow-100', bar: 'bg-yellow-500' },
    orange: { bg: 'bg-orange-100', bar: 'bg-orange-500' },
    red: { bg: 'bg-red-100', bar: 'bg-red-500' },
    blue: { bg: 'bg-blue-100', bar: 'bg-blue-500' },
    purple: { bg: 'bg-purple-100', bar: 'bg-purple-500' }
  };

  const colors = colorMap[color] || colorMap.blue;

  return (
    <div className={`w-full ${colors.bg} rounded-full overflow-hidden`} style={{ height }}>
      <div
        className={`h-full ${colors.bar} rounded-full transition-all duration-1000 ease-out ${
          showPulse ? 'animate-pulse' : ''
        }`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
};

// Badge d'alerte pour les quotas
const QuotaAlertBadge: React.FC<{ percentage: number; t: (key: string) => string }> = ({ percentage, t }) => {
  if (percentage >= 100) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full animate-pulse">
        <FiAlertCircle size={12} />
        {t('usage.alerts.limitReached')}
      </span>
    );
  }
  if (percentage >= 90) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
        <FiAlertTriangle size={12} />
        {t('usage.alerts.critical')}
      </span>
    );
  }
  if (percentage >= 80) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
        <FiAlertTriangle size={12} />
        {t('usage.alerts.warning')}
      </span>
    );
  }
  if (percentage >= 60) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
        <FiActivity size={12} />
        {t('usage.alerts.moderate')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
      <FiCheckCircle size={12} />
      {t('usage.alerts.ok')}
    </span>
  );
};

// Composant QuotaCard amélioré
interface QuotaCardProps {
  icon: React.ComponentType<{ size?: number }>;
  title: string;
  subtitle?: string;
  quota: QuotaInfo;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  resetsAt?: string;
  t: (key: string, options?: any) => string;
}

const QuotaCard: React.FC<QuotaCardProps> = ({ icon: Icon, title, subtitle, quota, color, resetsAt, t }) => {
  const percentage = getQuotaPercentage(quota);
  const quotaColor = getQuotaColor(percentage);
  const isWarning = percentage >= 80;
  const isCritical = percentage >= 90;

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div
      className={`bg-white rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-lg ${
        isCritical
          ? 'border-red-300 bg-red-50/30'
          : isWarning
          ? 'border-orange-300 bg-orange-50/30'
          : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{title}</h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {!quota.unlimited && <QuotaAlertBadge percentage={percentage} t={t} />}
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-4xl font-bold ${isCritical ? 'text-red-600' : 'text-gray-900'}`}>
            {quota.used.toLocaleString('fr-FR')}
          </span>
          {!quota.unlimited && (
            <>
              <span className="text-gray-400 text-xl">/</span>
              <span className="text-xl text-gray-600">{quota.limit.toLocaleString('fr-FR')}</span>
            </>
          )}
          {quota.unlimited && (
            <span className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
              {t('usage.usage.unlimited')}
            </span>
          )}
        </div>
        {!quota.unlimited && (
          <p className={`text-sm ${isWarning ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
            {quota.remaining.toLocaleString()} {quota.remaining > 1 ? t('usage.remainingPlural') : t('usage.remaining')}
          </p>
        )}
      </div>

      {!quota.unlimited && (
        <div className="space-y-2">
          <EnhancedProgressBar
            percentage={percentage}
            color={quotaColor}
            showPulse={isCritical}
            height={10}
          />
          <div className="flex justify-between items-center">
            <span
              className={`text-sm font-medium ${
                isCritical ? 'text-red-600' : isWarning ? 'text-orange-600' : 'text-gray-600'
              }`}
            >
              {t('usage.usedPercent', { percent: percentage })}
            </span>
            {resetsAt && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <FiRefreshCw size={12} />
                {t('usage.resetsAt', { date: new Date(resetsAt).toLocaleDateString() })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// Composant principal
const UsagePage: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [historyPeriod, setHistoryPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [historyData, setHistoryData] = useState<UsageHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { subscription, quotas, trial, alerts, upgradeSuggestion, loading, error, refresh } =
    useUsageDashboard();

  const loadHistory = useCallback(async (period: '7d' | '30d' | '90d') => {
    setHistoryLoading(true);
    try {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const data = await getUsageHistory(days as 7 | 30 | 90);
      setHistoryData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load usage history:', err?.response?.data || err);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory(historyPeriod);
  }, [historyPeriod, loadHistory]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4">
          <FiAlertCircle className="text-red-600 flex-shrink-0 mt-1" size={24} />
          <div>
            <h3 className="text-lg font-bold text-red-900 mb-2">{t('usage.error.title')}</h3>
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => refresh()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
            >
              <FiRefreshCw size={16} />
              {t('usage.error.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription || !quotas) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <p className="text-yellow-800">{t('usage.noData')}</p>
        </div>
      </div>
    );
  }

  // Calcul des pourcentages pour les cercles
  const archPercentage        = getQuotaPercentage(quotas.architectures);
  const projectsPercentage    = getQuotaPercentage(quotas.projects);
  const deploymentsPercentage = getQuotaPercentage(quotas.deployments);
  const pipelinePercentage    = getQuotaPercentage(quotas.pipeline_minutes);
  const membersPercentage     = quotas.members ? getQuotaPercentage(quotas.members) : 0;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('usage.header.title')}</h2>
            <p className="text-gray-600 mt-1">
              {t('usage.currentPlan')} :{' '}
              <span className="font-semibold text-blue-600">{getPlanName(subscription.plan)}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto">
            <button
              onClick={() => refresh()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title={t('usage.refresh')}
            >
              <FiRefreshCw size={20} />
            </button>
            {subscription.plan !== 'business' && (
              <button
                onClick={() => navigate('/settings/subscription')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 md:px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition flex items-center gap-2 shadow-md flex-1 md:flex-initial justify-center"
              >
                <FiArrowUp size={18} />
                <span className="hidden sm:inline">{t('usage.upgradeButton')}</span>
                <span className="sm:hidden">Upgrade</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Trial Alert (FREE plan only) */}
      {trial && subscription.plan === 'free' && (
        <div
          className={`mb-6 rounded-xl p-6 border-2 ${
            trial.is_expired
              ? 'bg-red-50 border-red-300'
              : trial.is_expiring_soon
              ? 'bg-orange-50 border-orange-300'
              : 'bg-blue-50 border-blue-300'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`p-3 rounded-full ${
                trial.is_expired
                  ? 'bg-red-100'
                  : trial.is_expiring_soon
                  ? 'bg-orange-100'
                  : 'bg-blue-100'
              }`}
            >
              <FiClock
                size={24}
                className={
                  trial.is_expired
                    ? 'text-red-600'
                    : trial.is_expiring_soon
                    ? 'text-orange-600'
                    : 'text-blue-600'
                }
              />
            </div>
            <div className="flex-1">
              <h3
                className={`text-lg font-bold mb-2 ${
                  trial.is_expired
                    ? 'text-red-900'
                    : trial.is_expiring_soon
                    ? 'text-orange-900'
                    : 'text-blue-900'
                }`}
              >
                {trial.is_expired
                  ? t('usage.trial.expired')
                  : trial.is_expiring_soon
                  ? t('usage.trial.expiringSoon')
                  : t('usage.trial.active')}
              </h3>
              <p
                className={
                  trial.is_expired
                    ? 'text-red-700'
                    : trial.is_expiring_soon
                    ? 'text-orange-700'
                    : 'text-blue-700'
                }
              >
                {trial.is_expired
                  ? t('usage.trial.expiredMessage')
                  : t('usage.trial.daysRemaining', { days: formatTrialDaysRemaining(trial.days_remaining) })}
              </p>
              {(trial.is_expired || trial.is_expiring_soon) && (
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/settings/subscription')}
                    className={`${
                      trial.is_expired ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'
                    } text-white px-6 py-2 rounded-lg transition font-medium`}
                  >
                    {trial.is_expired ? t('usage.trial.choosePlan') : t('usage.trial.upgradeNow')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alertes importantes */}
      {alerts && alerts.length > 0 && (
        <div className="mb-6 space-y-3">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`rounded-xl p-4 border-2 flex items-start gap-3 ${
                alert.severity === 'high'
                  ? 'bg-red-50 border-red-300 animate-pulse'
                  : alert.severity === 'medium'
                  ? 'bg-orange-50 border-orange-300'
                  : 'bg-yellow-50 border-yellow-300'
              }`}
            >
              {alert.severity === 'high' ? (
                <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
              ) : (
                <FiAlertTriangle
                  className={`flex-shrink-0 mt-0.5 ${
                    alert.severity === 'medium' ? 'text-orange-600' : 'text-yellow-600'
                  }`}
                  size={24}
                />
              )}
              <div className="flex-1">
                <p
                  className={`font-semibold ${
                    alert.severity === 'high'
                      ? 'text-red-900'
                      : alert.severity === 'medium'
                      ? 'text-orange-900'
                      : 'text-yellow-900'
                  }`}
                >
                  {alert.message}
                </p>
                {alert.current !== undefined && alert.limit !== undefined && (
                  <div className="mt-2">
                    <EnhancedProgressBar
                      percentage={Math.round((alert.current / alert.limit) * 100)}
                      color={alert.severity === 'high' ? 'red' : alert.severity === 'medium' ? 'orange' : 'yellow'}
                      height={6}
                    />
                    <p
                      className={`text-sm mt-1 ${
                        alert.severity === 'high'
                          ? 'text-red-700'
                          : alert.severity === 'medium'
                          ? 'text-orange-700'
                          : 'text-yellow-700'
                      }`}
                    >
                      {alert.current} / {alert.limit} ({Math.round((alert.current / alert.limit) * 100)}%)
                    </p>
                  </div>
                )}
              </div>
              {alert.severity === 'high' && (
                <button
                  onClick={() => navigate('/settings/subscription')}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition text-sm font-medium"
                >
                  Upgrade
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vue d'ensemble circulaire */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2">
          <FiActivity size={20} className="text-blue-600" />
          {t('usage.overview')}
        </h3>
        <div className="grid grid-cols-2 md:flex md:flex-wrap md:justify-around gap-4 md:gap-8">
          <CircularProgress
            percentage={archPercentage}
            color={getQuotaColor(archPercentage)}
            label={t('usage.resources.architectures')}
            value={quotas.architectures.unlimited ? '∞' : `${quotas.architectures.used}`}
            sublabel={quotas.architectures.unlimited ? t('usage.usage.unlimited') : `/ ${quotas.architectures.limit}`}
          />
          <CircularProgress
            percentage={projectsPercentage}
            color={getQuotaColor(projectsPercentage)}
            label={t('usage.resources.projects')}
            value={quotas.projects.unlimited ? '∞' : `${quotas.projects.used}`}
            sublabel={quotas.projects.unlimited ? t('usage.usage.unlimited') : `/ ${quotas.projects.limit}`}
          />
          <CircularProgress
            percentage={deploymentsPercentage}
            color={getQuotaColor(deploymentsPercentage)}
            label={t('usage.resources.deployments').replace(' ce mois', '')}
            value={quotas.deployments.unlimited ? '∞' : `${quotas.deployments.used}`}
            sublabel={quotas.deployments.unlimited ? t('usage.usage.unlimited') : `/ ${quotas.deployments.limit}`}
          />
          <CircularProgress
            percentage={pipelinePercentage}
            color={getQuotaColor(pipelinePercentage)}
            label={t('usage.resources.pipelineMinutes')}
            value={quotas.pipeline_minutes.unlimited ? '∞' : `${quotas.pipeline_minutes.used}`}
            sublabel={quotas.pipeline_minutes.unlimited ? t('usage.usage.unlimited') : `/ ${quotas.pipeline_minutes.limit}`}
          />
          {quotas.members && (
            <CircularProgress
              percentage={membersPercentage}
              color={getQuotaColor(membersPercentage)}
              label="Membres"
              value={quotas.members.unlimited ? '∞' : `${quotas.members.used}`}
              sublabel={quotas.members.unlimited ? 'Illimité' : `/ ${quotas.members.limit}`}
            />
          )}
        </div>
      </div>

      {/* Quota Cards détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        <QuotaCard icon={FiPackage} title={t('usage.resources.architectures')} quota={quotas.architectures} color="blue" t={t} />
        <QuotaCard icon={FiFolder} title={t('usage.resources.projects')} quota={quotas.projects} color="purple" t={t} />
        <QuotaCard
          icon={FiTrendingUp}
          title={t('usage.resources.deployments').replace(' ce mois', '')}
          subtitle={t('usage.thisMonth')}
          quota={quotas.deployments}
          color="green"
          resetsAt={quotas.deployments.resets_at}
          t={t}
        />
        <QuotaCard
          icon={FiZap}
          title={t('usage.resources.pipelineMinutes')}
          subtitle={t('usage.thisMonth')}
          quota={quotas.pipeline_minutes}
          color="orange"
          resetsAt={quotas.pipeline_minutes.resets_at}
          t={t}
        />
        {quotas.members && (
          <div className={`bg-white rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-lg ${
            membersPercentage >= 90 ? 'border-red-300 bg-red-50/30'
            : membersPercentage >= 80 ? 'border-orange-300 bg-orange-50/30'
            : 'border-gray-200'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="p-3 rounded-lg bg-indigo-100 text-indigo-600">
                  <FiUsers size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Membres</h3>
                  <p className="text-sm text-gray-500">
                    {quotas.members.included_seats != null
                      ? `${quotas.members.included_seats} inclus dans le plan`
                      : 'Sièges membres'}
                  </p>
                </div>
              </div>
              {!quotas.members.unlimited && <QuotaAlertBadge percentage={membersPercentage} t={t} />}
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-2 mb-1">
                <span className={`text-4xl font-bold ${membersPercentage >= 90 ? 'text-red-600' : 'text-gray-900'}`}>
                  {quotas.members.used}
                </span>
                {!quotas.members.unlimited && (
                  <>
                    <span className="text-gray-400 text-xl">/</span>
                    <span className="text-xl text-gray-600">{quotas.members.limit}</span>
                  </>
                )}
                {quotas.members.unlimited && (
                  <span className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">Illimité</span>
                )}
              </div>
              {!quotas.members.unlimited && (
                <p className={`text-sm ${membersPercentage >= 80 ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                  {quotas.members.remaining} place{quotas.members.remaining > 1 ? 's' : ''} restante{quotas.members.remaining > 1 ? 's' : ''}
                </p>
              )}
            </div>

            {!quotas.members.unlimited && (
              <div className="space-y-2">
                <EnhancedProgressBar percentage={membersPercentage} color={getQuotaColor(membersPercentage)} height={10} />
                <div className="flex justify-between items-center">
                  <span className={`text-sm font-medium ${membersPercentage >= 90 ? 'text-red-600' : membersPercentage >= 80 ? 'text-orange-600' : 'text-gray-600'}`}>
                    {membersPercentage}% utilisé
                  </span>
                  {(quotas.members.additional_seats_used ?? 0) > 0 && quotas.members.addon_price_cents && (
                    <span className="text-xs text-orange-600 font-medium">
                      +{quotas.members.additional_seats_used} siège{(quotas.members.additional_seats_used ?? 0) > 1 ? 's' : ''} additionnel{(quotas.members.additional_seats_used ?? 0) > 1 ? 's' : ''} ({(quotas.members.addon_price_cents / 100).toFixed(0)}€/mois chacun)
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-100">
              <a href="/settings/members" className="text-sm text-blue-600 hover:underline font-medium">
                Gérer les membres →
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Historique d'utilisation */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 md:mb-6">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FiCalendar size={20} className="text-blue-600" />
            {t('usage.history.title')}
          </h3>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {(['7d', '30d', '90d'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setHistoryPeriod(period)}
                className={`flex-1 sm:flex-initial px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium transition ${
                  historyPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {period === '7d' ? t('usage.history.days7') : period === '30d' ? t('usage.history.days30') : t('usage.history.days90')}
              </button>
            ))}
          </div>
        </div>

        {/* Graphique Area */}
        <div className="h-48 md:h-64 mb-6 -mx-2 md:mx-0">
          {historyLoading ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">Chargement…</div>
          ) : historyData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">Aucune donnée pour cette période</div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={historyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorArch" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorDeploy" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="architectures"
                name="Architectures"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorArch)"
              />
              <Area
                type="monotone"
                dataKey="deployments"
                name="Déploiements"
                stroke="#22c55e"
                fillOpacity={1}
                fill="url(#colorDeploy)"
              />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </div>

        {/* Graphique Barres pour Minutes Pipeline */}
        <h4 className="text-sm md:text-md font-semibold text-gray-700 mb-4">{t('usage.history.pipelineTitle')}</h4>
        <div className="h-40 md:h-48 -mx-2 md:mx-0">
          {historyLoading ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">Chargement…</div>
          ) : historyData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">Aucune donnée pour cette période</div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={historyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="pipelineMinutes" name="Minutes" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Upgrade Suggestion */}
      {upgradeSuggestion && (
        <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full shadow-lg">
              <FiArrowUp size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t('usage.upgradeSuggestion.title', { plan: getPlanName(upgradeSuggestion.recommended_plan) })}
              </h3>
              <p className="text-gray-700 mb-4">{upgradeSuggestion.reason}</p>
              <button
                onClick={() => navigate('/settings/subscription')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-medium shadow-md"
              >
                {t('usage.upgradeSuggestion.button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsagePage;
